import settings from '@solinkcorp/settings';
import { type ValueOf } from 'type-fest';

export type VirtualTabletAction = 'arm' | 'disarm';

export type VirtualTabletLocationInput =
  { type: 'gps'; lat: number; lng: number } | { type: 'storeNumber'; value: string };

// delaySeconds is the only reliable field; rest of the response is unconfirmed
// backend internals. /arm only — disarm takes effect immediately.
export interface VirtualTabletActionResult {
  delaySeconds?: number;
}

export const VirtualTabletErrorCode = {
  // Uniform across wrong PIN/location/site — SL-16724: never reveal which part failed.
  NO_MATCH: 'no_match',
  INVALID_PIN_FORMAT: 'invalid_pin_format',
  NETWORK_ERROR: 'network_error',
} as const;
export type VirtualTabletErrorCode = ValueOf<typeof VirtualTabletErrorCode>;

export type VirtualTabletError = { error: 'no_match' | 'invalid_pin_format' | 'network_error' };

// Backend returns 401 for every failure, ambiguous by design (SL-16723).
export type VirtualTabletResult<T> =
  { ok: true; data: T } | { ok: false; error: VirtualTabletError };

export interface VirtualTabletApiClient {
  validate(
    siteId: string,
    action: VirtualTabletAction,
    pin: string,
    location: VirtualTabletLocationInput,
  ): Promise<VirtualTabletResult<VirtualTabletActionResult>>;
}

// Unauthenticated page never calls settings.update(), so dtmfPinUrl's hardcoded
// per-environment defaults apply directly. /disarm confirmed live; /arm presumed to mirror it.
const BASE_PATH: Record<VirtualTabletAction, string> = { arm: '/arm', disarm: '/disarm' };

async function parseResponse<T>(response: Response): Promise<VirtualTabletResult<T>> {
  let body: any = null;
  try {
    body = await response.json();
  } catch {
    // no/invalid JSON body
  }

  if (response.ok) {
    return { ok: true, data: body as T };
  }

  if (body?.error) {
    return { ok: false, error: body as VirtualTabletError };
  }

  return { ok: false, error: { error: 'network_error' } };
}

export class VirtualTabletApi implements VirtualTabletApiClient {
  async validate(
    siteId: string,
    action: VirtualTabletAction,
    pin: string,
    location: VirtualTabletLocationInput,
  ): Promise<VirtualTabletResult<VirtualTabletActionResult>> {
    try {
      const response = await fetch(
        `${settings.dtmfPinUrl}${BASE_PATH[action]}/${encodeURIComponent(siteId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pin, location }),
        },
      );
      return parseResponse(response);
    } catch {
      return { ok: false, error: { error: 'network_error' } };
    }
  }
}

const virtualTabletApi: VirtualTabletApiClient = new VirtualTabletApi();

export default virtualTabletApi;
